import type { StateView } from '@quiz/shared';
import type { PlayerSession } from '../lib/session.js';
import { Card, Logo } from '../components/ui.js';
import { useMediaPrefetch } from './useMedia.js';

/**
 * The holding area.
 *
 * Everyone who has paid and entered their code waits here. The whole quiz is
 * already downloaded to the phone, sealed -- when the host starts the first
 * round, the next poll drops them straight into question one.
 *
 * Deliberately almost empty: no prize, no round list, nothing to read ahead
 * about. It answers one question, "am I in?", and then gets out of the way.
 *
 * It is also where the night's media downloads. This is the only window wide
 * enough: a round of six 480p videos is 900MB across 75 phones, which is 40Mbps
 * squeezed into a three-minute round break and 6Mbps spread across a
 * twenty-minute door. Only the second of those is a number a pub access point
 * will meet. What arrives is ciphertext, so it buys nothing but resilience --
 * the key still does not come until the host opens the question.
 */
export function LobbyScreen({ session, state }: { session: PlayerSession; state: StateView }) {
  const media = useMediaPrefetch(session.quiz, session.eventId, session.playerToken, state.audioMode);
  const pct =
    media && media.bytesTotal > 0 ? Math.round((media.bytesDone / media.bytesTotal) * 100) : 0;
  const settled = media !== null && media.done === media.total;

  return (
    <div className="flex flex-1 flex-col gap-4">
      <Logo className="mt-2 h-16" />

      <Card className="text-center">
        <p className="text-xs uppercase tracking-wide text-muted">You&rsquo;re in as</p>
        <p className="mt-1 text-3xl font-bold">{session.callSign}</p>
      </Card>

      <Card className="text-center">
        <p className="text-4xl font-bold tabular-nums">{state.playerCount}</p>
        <p className="text-sm text-muted">
          {state.playerCount === 1 ? 'player waiting' : 'players waiting'}
        </p>
      </Card>

      {media && media.total > 0 && (
        <Card>
          <div className="flex items-baseline justify-between text-sm">
            <span className="font-semibold">
              {settled ? 'Ready to play' : 'Getting tonight\u2019s pictures and clips'}
            </span>
            <span className="tabular-nums text-muted">
              {settled ? `${media.total} loaded` : `${pct}%`}
            </span>
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-ink-2">
            <div
              className="h-full rounded-full bg-go transition-[width] duration-500"
              style={{ width: `${Math.max(pct, 2)}%` }}
            />
          </div>
          <p className="mt-2 text-xs text-muted">
            {settled
              ? 'Downloaded now so the quiz keeps working if the wifi drops mid-round.'
              : 'You can start without this finishing — it will catch up.'}
          </p>
          {media.failed > 0 && (
            <p className="mt-1 text-xs text-flag">
              {media.failed} did not download. They will be fetched when the host opens them.
            </p>
          )}
        </Card>
      )}

      <div className="flex flex-1 flex-col items-center justify-center gap-2 text-center">
        <p className="animate-pulse text-lg text-muted">Waiting for the host to start…</p>
        <p className="text-sm text-muted">
          Keep this page open. The first question will appear here.
        </p>
      </div>
    </div>
  );
}
